import {
  InlineVideoPlayer
} from "./chunk-X2UJPGLE.js";
import {
  VideoPlayer
} from "./chunk-WGA24APS.js";
import {
  ItemTypes
} from "./chunk-IX3ZB4XQ.js";
import {
  ImageLightbox
} from "./chunk-I3CRLQRZ.js";
import {
  MenuIcon
} from "./chunk-3AGD6MKM.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./chunk-IBYHKJKK.js";
import {
  Icon
} from "./chunk-7IEFGXFA.js";
import {
  cn
} from "./chunk-SBEJSA5Q.js";

// src/components/canvas/editor/sidebar/CanvasSidebarAssets.tsx
import { useState, useRef, useEffect } from "react";
import { useDrag } from "react-dnd";
import { jsx, jsxs } from "react/jsx-runtime";
var setFileDragData = (event, file) => {
  if (!file.publicUrl) return;
  event.dataTransfer.setData("application/x-genie-file", file.publicUrl);
  event.dataTransfer.setData("application/x-genie-file-meta", JSON.stringify({ uuid: file.uuid, filename: file.filename, contentType: file.contentType }));
};
var fileIcon = (file) => file.contentType?.startsWith("video/") ? "video" : file.contentType?.startsWith("audio/") || /\.(mp3|wav|ogg|flac|m4a|aac)$/i.test(file.filename) ? "music" : file.contentType?.startsWith("model/") || /\.(glb|gltf|obj|stl|fbx|ply)$/i.test(file.filename) ? "box" : "image";
function DraggableFileListItem({ file, onDownload, onDelete, onRename, onDuplicate, onPreview }) {
  const isAiGenerated = !!file.nodeId;
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SIDEBAR_FILE,
    canDrag: Boolean(file.publicUrl),
    item: { uuid: file.uuid, filename: file.filename, contentType: file.contentType, publicUrl: file.publicUrl, metadata: file.metadata },
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: drag,
      onDragStart: (event) => setFileDragData(event, file),
      className: cn("flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 group cursor-grab active:cursor-grabbing", isDragging && "opacity-50"),
      onClick: () => onPreview?.(file),
      onKeyDown: (e) => {
        if (e.key === "Enter") onPreview?.(file);
      },
      tabIndex: 0,
      role: "button",
      "aria-label": `Preview ${file.filename}`,
      children: [
        /* @__PURE__ */ jsx(Icon, { name: fileIcon(file), className: "h-4 w-4 flex-shrink-0 text-muted-foreground" }),
        /* @__PURE__ */ jsx("span", { className: "text-sm flex-1 truncate", children: file.filename }),
        isAiGenerated && /* @__PURE__ */ jsx(Icon, { name: "sparkles", className: "h-3 w-3 text-muted-foreground flex-shrink-0" }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100", children: [
          /* @__PURE__ */ jsx("button", { className: "flex h-5 w-5 items-center justify-center", title: "Download", disabled: !file.publicUrl, onClick: (e) => {
            e.stopPropagation();
            onDownload(file);
          }, children: /* @__PURE__ */ jsx(Icon, { name: "download", className: "h-4 w-4" }) }),
          onDelete && /* @__PURE__ */ jsx("button", { className: "flex h-5 w-5 items-center justify-center text-destructive", title: "Delete", onClick: (e) => {
            e.stopPropagation();
            void onDelete(file.uuid);
          }, children: /* @__PURE__ */ jsx(Icon, { name: "trash-2", className: "h-4 w-4" }) }),
          /* @__PURE__ */ jsx(FileActions, { file, onRename, onDuplicate })
        ] })
      ]
    }
  );
}
function DraggableFileGridItem({ file, folders, getThumbnailUrl, renderPreview, onDownload, onDelete, onMoveToFolder, onRename, onDuplicate, onPreview }) {
  const isAiGenerated = !!file.nodeId;
  const isVideo = file.contentType?.startsWith("video/") ?? false;
  const thumbnailUrl = file.contentType?.startsWith("image/") ? getThumbnailUrl(file) : null;
  const hostPreview = !isVideo && !thumbnailUrl ? renderPreview?.(file) : null;
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SIDEBAR_FILE,
    canDrag: Boolean(file.publicUrl),
    item: { uuid: file.uuid, filename: file.filename, contentType: file.contentType, publicUrl: file.publicUrl, metadata: file.metadata },
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: drag,
      onDragStart: (event) => setFileDragData(event, file),
      className: cn("rounded-lg group cursor-grab active:cursor-grabbing", isDragging && "opacity-50"),
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn("relative aspect-[4/3] bg-muted rounded-lg overflow-hidden", !isVideo && "cursor-pointer"),
            onClick: isVideo ? void 0 : () => onPreview?.(file),
            onKeyDown: isVideo ? void 0 : (e) => {
              if (e.key === "Enter") onPreview?.(file);
            },
            tabIndex: isVideo ? void 0 : 0,
            role: isVideo ? void 0 : "button",
            "aria-label": isVideo ? void 0 : `Preview ${file.filename}`,
            children: isVideo && file.publicUrl ? (
              // Plays in place; the expand control opens the large player.
              /* @__PURE__ */ jsx(InlineVideoPlayer, { src: file.publicUrl, filename: file.filename, onExpand: () => onPreview?.(file) })
            ) : thumbnailUrl ? /* @__PURE__ */ jsx("img", { src: thumbnailUrl, alt: file.filename, className: "w-full h-full object-cover" }) : hostPreview ? hostPreview : /* @__PURE__ */ jsx("div", { className: "w-full h-full flex items-center justify-center text-muted-foreground", children: /* @__PURE__ */ jsx(Icon, { name: file.contentType?.startsWith("model/") ? "box" : file.contentType?.startsWith("audio/") ? "music" : "image", className: "h-8 w-8 opacity-40" }) })
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 pt-2", children: [
          /* @__PURE__ */ jsx(Icon, { name: fileIcon(file), className: "h-4 w-4 flex-shrink-0 mt-0.5" }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx("span", { className: "text-sm font-medium truncate", children: file.filename }),
            isAiGenerated && /* @__PURE__ */ jsx(Icon, { name: "sparkles", className: "h-3 w-3 flex-shrink-0 text-muted-foreground" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 flex-shrink-0", children: [
            /* @__PURE__ */ jsx("button", { className: "flex h-5 w-5 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity", title: "Download", disabled: !file.publicUrl, onClick: (e) => {
              e.stopPropagation();
              onDownload(file);
            }, children: /* @__PURE__ */ jsx(Icon, { name: "download", className: "h-4 w-4" }) }),
            onDelete && /* @__PURE__ */ jsx("button", { className: "flex h-5 w-5 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-destructive", title: "Delete", onClick: (e) => {
              e.stopPropagation();
              void onDelete(file.uuid);
            }, children: /* @__PURE__ */ jsx(Icon, { name: "trash-2", className: "h-4 w-4" }) }),
            /* @__PURE__ */ jsx(FileActions, { file, onRename, onDuplicate }),
            onMoveToFolder && folders.length > 0 && /* @__PURE__ */ jsxs(DropdownMenu, { children: [
              /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx("button", { className: "flex h-5 w-5 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity", title: "Move to folder", onClick: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsx(Icon, { name: "folder-input", className: "h-4 w-4" }) }) }),
              /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-48", children: [
                /* @__PURE__ */ jsx("div", { className: "px-2 py-1.5 text-xs text-muted-foreground font-medium", children: "Move to folder" }),
                /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
                folders.map((folder) => /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: () => onMoveToFolder(file, folder.id), children: [
                  /* @__PURE__ */ jsx(MenuIcon, { name: "folder", className: "h-4 w-4 mr-2" }),
                  folder.name
                ] }, folder.id))
              ] })
            ] })
          ] })
        ] })
      ]
    }
  );
}
function FileActions({ file, onRename, onDuplicate }) {
  if (!onRename && !onDuplicate) return null;
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx("button", { type: "button", title: `Actions for ${file.filename}`, onClick: (event) => event.stopPropagation(), className: "flex h-5 w-5 items-center justify-center", children: /* @__PURE__ */ jsx(Icon, { name: "more-horizontal", className: "h-4 w-4" }) }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", children: [
      onRename && /* @__PURE__ */ jsx(DropdownMenuItem, { onClick: (event) => {
        event.stopPropagation();
        onRename(file);
      }, children: "Rename" }),
      onDuplicate && /* @__PURE__ */ jsx(DropdownMenuItem, { onClick: (event) => {
        event.stopPropagation();
        onDuplicate(file);
      }, children: "Duplicate" })
    ] })
  ] });
}
var CanvasSidebarAssets = ({
  onOpenFile,
  renderFilePreview,
  locationControl,
  locationKey,
  onOpenFolder,
  error,
  onRetry,
  uploadLabel = "Upload file",
  uploadAccept = "image/*,video/*",
  onRenameFile,
  onDuplicateFile,
  folders,
  foldersLoading,
  folderCountsKnown = true,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onDuplicateFolder,
  files,
  filesLoading,
  onMoveFileToFolder,
  onUploadFile,
  onDeleteFile
}) => {
  const [openFolderId, setOpenFolderId] = useState(null);
  const openFolder = folders.find((f) => f.id === openFolderId) ?? null;
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const newFolderInputRef = useRef(null);
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const renameInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest");
  const [viewMode, setViewMode] = useState("grid");
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);
  const fileListRef = useRef(null);
  useEffect(() => {
    setOpenFolderId(null);
    setPreviewFile(null);
    setIsCreatingFolder(false);
    setRenamingFolderId(null);
  }, [locationKey]);
  useEffect(() => {
    const el = fileListRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight;
      const canScrollUp = el.scrollTop > 0;
      if (e.deltaY > 0 && canScrollDown || e.deltaY < 0 && canScrollUp) {
        e.stopPropagation();
      }
    };
    el.addEventListener("wheel", handleWheel);
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);
  useEffect(() => {
    if (isCreatingFolder) {
      newFolderInputRef.current?.focus();
    }
  }, [isCreatingFolder]);
  useEffect(() => {
    if (renamingFolderId) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingFolderId]);
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name || !onCreateFolder) return;
    const result = await onCreateFolder(name);
    if (result) {
      setNewFolderName("");
      setIsCreatingFolder(false);
    }
  };
  const handleRenameFolder = async (folderId) => {
    const name = renameFolderName.trim();
    if (!name || !onRenameFolder) return;
    if (await onRenameFolder(folderId, name)) setRenamingFolderId(null);
  };
  const startRename = (folder) => {
    setRenamingFolderId(folder.id);
    setRenameFolderName(folder.name);
  };
  const getThumbnailUrl = (file) => {
    const versions = file.metadata?.versions;
    const preview = versions?.preview;
    return preview?.url ?? file.publicUrl ?? null;
  };
  const handleDownload = (file) => {
    if (!file.publicUrl) return;
    const a = document.createElement("a");
    a.href = file.publicUrl;
    a.download = file.filename;
    a.target = "_blank";
    a.click();
  };
  const handleMoveToFolder = async (file, folderId) => {
    await onMoveFileToFolder?.(file.uuid, folderId);
  };
  const handleFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadFile) return;
    await onUploadFile(file);
    e.target.value = "";
  };
  const filteredFolders = folders.filter((folder) => !searchQuery || folder.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredFiles = files.filter((f) => openFolderId ? f.folderId === openFolderId : f.folderId === null || f.folderId === void 0).filter((f) => !searchQuery || f.filename.toLowerCase().includes(searchQuery.toLowerCase())).sort((a, b) => {
    if (sortOrder === "name") return a.filename.localeCompare(b.filename);
    if (sortOrder === "oldest") return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col min-h-0 overflow-hidden flex-1", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex-shrink-0", children: [
      locationControl,
      /* @__PURE__ */ jsx(
        "input",
        {
          ref: fileInputRef,
          type: "file",
          accept: uploadAccept,
          className: "hidden",
          onChange: handleFileInputChange
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 px-2 pt-2 pb-3", children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            className: cn(
              "flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors",
              showSearch && "bg-accent"
            ),
            title: "Search",
            onClick: () => setShowSearch((v) => !v),
            children: /* @__PURE__ */ jsx(Icon, { name: "search", className: "h-5 w-5" })
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            className: "flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors",
            title: uploadLabel,
            onClick: () => fileInputRef.current?.click(),
            disabled: !onUploadFile,
            children: /* @__PURE__ */ jsx(Icon, { name: "upload", className: "h-5 w-5" })
          }
        ),
        onCreateFolder && /* @__PURE__ */ jsx(
          "button",
          {
            className: "flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors",
            title: "New folder",
            onClick: () => {
              setIsCreatingFolder(true);
              setNewFolderName("");
            },
            children: /* @__PURE__ */ jsx(Icon, { name: "folder-plus", className: "h-5 w-5" })
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            className: cn(
              "flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors",
              viewMode === "list" && "bg-accent"
            ),
            title: viewMode === "grid" ? "Switch to list view" : "Switch to grid view",
            onClick: () => setViewMode((v) => v === "grid" ? "list" : "grid"),
            children: /* @__PURE__ */ jsx(Icon, { name: viewMode === "grid" ? "list" : "grid-2x2", className: "h-5 w-5" })
          }
        ),
        /* @__PURE__ */ jsxs(DropdownMenu, { children: [
          /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx("button", { className: "flex h-9 flex-1 items-center justify-center rounded-lg bg-muted hover:bg-accent transition-colors", title: "Sort", children: /* @__PURE__ */ jsx(Icon, { name: "arrow-up-down", className: "h-5 w-5" }) }) }),
          /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-36", children: [
            /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: () => setSortOrder("newest"), children: [
              sortOrder === "newest" && /* @__PURE__ */ jsx(MenuIcon, { name: "check", className: "h-4 w-4 mr-2" }),
              sortOrder !== "newest" && /* @__PURE__ */ jsx("span", { className: "w-6" }),
              "Newest first"
            ] }),
            /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: () => setSortOrder("oldest"), children: [
              sortOrder === "oldest" && /* @__PURE__ */ jsx(MenuIcon, { name: "check", className: "h-4 w-4 mr-2" }),
              sortOrder !== "oldest" && /* @__PURE__ */ jsx("span", { className: "w-6" }),
              "Oldest first"
            ] }),
            /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: () => setSortOrder("name"), children: [
              sortOrder === "name" && /* @__PURE__ */ jsx(MenuIcon, { name: "check", className: "h-4 w-4 mr-2" }),
              sortOrder !== "name" && /* @__PURE__ */ jsx("span", { className: "w-6" }),
              "By name"
            ] })
          ] })
        ] })
      ] }),
      showSearch && /* @__PURE__ */ jsx("div", { className: "px-2 pb-2", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted", children: [
        /* @__PURE__ */ jsx(Icon, { name: "search", className: "h-4 w-4 text-muted-foreground flex-shrink-0" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            value: searchQuery,
            onChange: (e) => setSearchQuery(e.target.value),
            placeholder: "Search files...",
            className: "flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground",
            autoFocus: true
          }
        ),
        searchQuery && /* @__PURE__ */ jsx("button", { onClick: () => setSearchQuery(""), className: "text-muted-foreground hover:text-foreground", children: /* @__PURE__ */ jsx(Icon, { name: "x", className: "h-4 w-4" }) })
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: fileListRef,
        className: "flex-1 min-h-0 overflow-y-auto px-2 pt-2 pb-2 space-y-2\n          [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar]:bg-transparent\n          [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full\n          hover:[&::-webkit-scrollbar-thumb]:bg-neutral-300 dark:hover:[&::-webkit-scrollbar-thumb]:bg-neutral-600\n          [&::-webkit-scrollbar-track]:bg-transparent",
        children: [
          error && /* @__PURE__ */ jsxs("p", { role: "alert", className: "rounded-md bg-destructive/10 p-3 text-sm text-destructive", children: [
            error,
            onRetry && /* @__PURE__ */ jsx("button", { type: "button", onClick: onRetry, className: "ml-2 underline", children: "Retry" })
          ] }),
          openFolder && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 py-1.5 px-1 mb-1", children: [
            /* @__PURE__ */ jsxs(
              "button",
              {
                className: "flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors",
                onClick: () => setOpenFolderId(null),
                children: [
                  /* @__PURE__ */ jsx(Icon, { name: "chevron-left", className: "h-4 w-4" }),
                  /* @__PURE__ */ jsx("span", { children: "Back" })
                ]
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "text-sm text-muted-foreground", children: "/" }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 min-w-0", children: [
              /* @__PURE__ */ jsx(Icon, { name: "folder", className: "h-4 w-4 flex-shrink-0" }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-medium truncate", children: openFolder.name })
            ] })
          ] }),
          isCreatingFolder && !openFolderId && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 py-2 px-3 rounded-md border border-border bg-card", children: [
            /* @__PURE__ */ jsx(Icon, { name: "folder", className: "h-5 w-5 flex-shrink-0 text-muted-foreground" }),
            /* @__PURE__ */ jsxs("div", { className: "flex flex-col flex-1 min-w-0", children: [
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref: newFolderInputRef,
                  type: "text",
                  value: newFolderName,
                  onChange: (e) => setNewFolderName(e.target.value),
                  onKeyDown: (e) => {
                    if (e.key === "Enter") handleCreateFolder();
                    if (e.key === "Escape") {
                      setIsCreatingFolder(false);
                      setNewFolderName("");
                    }
                  },
                  onBlur: () => {
                    if (!newFolderName.trim()) {
                      setIsCreatingFolder(false);
                    }
                  },
                  placeholder: "Folder name",
                  className: "bg-transparent text-sm font-medium outline-none placeholder:text-muted-foreground"
                }
              ),
              /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "0 files" })
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: handleCreateFolder,
                className: "flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground",
                children: /* @__PURE__ */ jsx(Icon, { name: "check", className: "h-4 w-4" })
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => {
                  setIsCreatingFolder(false);
                  setNewFolderName("");
                },
                className: "flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground",
                children: /* @__PURE__ */ jsx(Icon, { name: "x", className: "h-4 w-4" })
              }
            )
          ] }),
          openFolderId ? null : foldersLoading && folders.length === 0 ? /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-6 text-muted-foreground", children: /* @__PURE__ */ jsx(Icon, { name: "loader-2", className: "h-4 w-4 animate-spin" }) }) : folders.length === 0 && !isCreatingFolder ? null : filteredFolders.map((folder) => /* @__PURE__ */ jsxs(
            "div",
            {
              className: "flex items-center gap-3 py-2 px-3 rounded-md border border-border bg-card hover:border-foreground/20 cursor-pointer group transition-colors",
              onClick: () => onOpenFolder ? onOpenFolder(folder) : setOpenFolderId(folder.id),
              role: "button",
              tabIndex: 0,
              "aria-label": `Open folder ${folder.name}`,
              onKeyDown: (event) => {
                if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  onOpenFolder ? onOpenFolder(folder) : setOpenFolderId(folder.id);
                }
              },
              children: [
                /* @__PURE__ */ jsx(Icon, { name: "folder", className: "h-5 w-5 flex-shrink-0" }),
                /* @__PURE__ */ jsxs("div", { className: "flex flex-col flex-1 min-w-0", children: [
                  renamingFolderId === folder.id ? /* @__PURE__ */ jsx(
                    "input",
                    {
                      ref: renameInputRef,
                      type: "text",
                      value: renameFolderName,
                      onChange: (e) => setRenameFolderName(e.target.value),
                      onKeyDown: (e) => {
                        if (e.key === "Enter") handleRenameFolder(folder.id);
                        if (e.key === "Escape") setRenamingFolderId(null);
                      },
                      onBlur: () => handleRenameFolder(folder.id),
                      onClick: (e) => e.stopPropagation(),
                      className: "bg-transparent text-sm font-medium outline-none border-b border-border w-full"
                    }
                  ) : /* @__PURE__ */ jsx("span", { className: "text-sm font-medium truncate", children: folder.name }),
                  /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: folderCountsKnown ? `${folder.fileCount} ${folder.fileCount === 1 ? "file" : "files"}` : "Folder" })
                ] }),
                /* @__PURE__ */ jsxs(DropdownMenu, { children: [
                  /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
                    "button",
                    {
                      className: "flex h-5 w-5 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0",
                      onClick: (e) => e.stopPropagation(),
                      children: /* @__PURE__ */ jsx(Icon, { name: "more-horizontal", className: "h-4 w-4" })
                    }
                  ) }),
                  /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-40", children: [
                    /* @__PURE__ */ jsxs(DropdownMenuItem, { disabled: !onRenameFolder, onClick: (event) => {
                      event.stopPropagation();
                      startRename(folder);
                    }, children: [
                      /* @__PURE__ */ jsx(MenuIcon, { name: "pencil", className: "h-4 w-4 mr-2" }),
                      "Rename"
                    ] }),
                    /* @__PURE__ */ jsxs(DropdownMenuItem, { disabled: !onDuplicateFolder, onClick: (event) => {
                      event.stopPropagation();
                      void onDuplicateFolder?.(folder.id);
                    }, children: [
                      /* @__PURE__ */ jsx(MenuIcon, { name: "copy", className: "h-4 w-4 mr-2" }),
                      "Duplicate"
                    ] }),
                    /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
                    /* @__PURE__ */ jsxs(
                      DropdownMenuItem,
                      {
                        disabled: !onDeleteFolder,
                        onClick: (event) => {
                          event.stopPropagation();
                          void onDeleteFolder?.(folder.id);
                        },
                        className: "text-destructive focus:text-destructive",
                        children: [
                          /* @__PURE__ */ jsx(MenuIcon, { name: "trash-2", className: "h-4 w-4 mr-2" }),
                          "Delete"
                        ]
                      }
                    )
                  ] })
                ] })
              ]
            },
            folder.id
          )),
          filesLoading && files.length === 0 && /* @__PURE__ */ jsx("div", { className: "space-y-3", children: [1, 2].map((i) => /* @__PURE__ */ jsxs("div", { className: "rounded-lg animate-pulse", children: [
            /* @__PURE__ */ jsx("div", { className: "aspect-[4/3] bg-muted rounded-lg" }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 pt-2", children: [
              /* @__PURE__ */ jsx("div", { className: "h-4 w-4 bg-muted rounded flex-shrink-0" }),
              /* @__PURE__ */ jsx("div", { className: "h-4 bg-muted rounded flex-1" })
            ] })
          ] }, i)) }),
          !error && !filesLoading && !foldersLoading && filteredFiles.length === 0 && (openFolderId || filteredFolders.length === 0) && /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center py-8 text-center text-muted-foreground", children: [
            /* @__PURE__ */ jsx(Icon, { name: "image", className: "h-8 w-8 mb-2 opacity-40" }),
            /* @__PURE__ */ jsx("p", { className: "text-sm font-medium", children: searchQuery ? "No matching files" : openFolderId ? "Folder is empty" : "No files yet" }),
            /* @__PURE__ */ jsx("p", { className: "text-xs mt-1 opacity-70", children: searchQuery ? "Try a different search." : locationControl ? "Choose another location or upload a file." : openFolderId ? "Move files here or generate new ones" : "Generated images will appear here" })
          ] }),
          viewMode === "list" ? filteredFiles.map((file) => /* @__PURE__ */ jsx(
            DraggableFileListItem,
            {
              file,
              onPreview: (file2) => {
                if (!onOpenFile?.(file2)) setPreviewFile(file2);
              },
              onDownload: handleDownload,
              onDelete: onDeleteFile ? (uuid) => void onDeleteFile(uuid) : void 0,
              onRename: onRenameFile,
              onDuplicate: onDuplicateFile
            },
            file.uuid
          )) : filteredFiles.map((file) => /* @__PURE__ */ jsx(
            DraggableFileGridItem,
            {
              file,
              onPreview: (file2) => {
                if (!onOpenFile?.(file2)) setPreviewFile(file2);
              },
              folders,
              getThumbnailUrl,
              renderPreview: renderFilePreview,
              onDownload: handleDownload,
              onDelete: onDeleteFile ? (uuid) => void onDeleteFile(uuid) : void 0,
              onRename: onRenameFile,
              onDuplicate: onDuplicateFile,
              onMoveToFolder: onMoveFileToFolder ? (f, folderId) => {
                void handleMoveToFolder(f, folderId);
              } : void 0
            },
            file.uuid
          ))
        ]
      }
    ),
    previewFile?.publicUrl && previewFile.contentType?.startsWith("video/") && /* @__PURE__ */ jsx(
      VideoPlayer,
      {
        isOpen: true,
        src: previewFile.publicUrl,
        filename: previewFile.filename,
        onClose: () => setPreviewFile(null),
        onDownload: () => handleDownload(previewFile)
      }
    ),
    previewFile?.publicUrl && previewFile.contentType?.startsWith("image/") && /* @__PURE__ */ jsx(
      ImageLightbox,
      {
        isOpen: true,
        images: [{ id: previewFile.uuid, url: getThumbnailUrl(previewFile) ?? previewFile.publicUrl, alt: previewFile.filename }],
        onClose: () => setPreviewFile(null)
      }
    )
  ] });
};

export {
  CanvasSidebarAssets
};
//# sourceMappingURL=chunk-4FHQXWXN.js.map